import { useState, useCallback, useEffect, useMemo } from "react";
import { useTimer } from "./useTimer";
import type { Word, LogicRule } from "@/lib/game-utils";
import { useExternalPuzzleSource } from "./useExternalPuzzleSource";

export function useGameBoard(
  words: Word[],
  logicRules: LogicRule[],
  correctOrder: Word[],
  correctRule: LogicRule,
  onComplete: (
    result: "perfect" | "wrong",
    solveTime: number,
    selectedWords: Array<{ word: Word; order: number }>,
    selectedRule: LogicRule,
    filteredRules: LogicRule[]
  ) => void,
  externalDataUrl?: string
) {
  // If we have external data, use it
  const { externalPuzzle } = useExternalPuzzleSource(externalDataUrl);

  const [selectedWords, setSelectedWords] = useState<
    Array<{ word: Word; order: number }>
  >([]);
  const [selectedRule, setSelectedRule] = useState<LogicRule | null>(null);
  const [uniqueLogicRules, setUniqueLogicRules] = useState<LogicRule[]>(logicRules);
  const [showingPreview, setShowingPreview] = useState<boolean>(false);

  // Timer for tracking solve time
  const { elapsedTime, setElapsedTime } = useTimer();

  // Reset selected words when words change
  useEffect(() => {
    setSelectedWords([]);
  }, [words]);

  // Set the only available logic rule as selected
  useEffect(() => {
    if (logicRules.length > 0) {
      setSelectedRule(logicRules[0]);
    }
  }, [logicRules]);

  // Handle word click for selection/deselection
  const handleWordClick = useCallback(
    (word: Word) => {
      console.log("Word clicked in useGameBoard:", word);
      setSelectedWords((prevWords) => {
        // If already selected, remove it
        const existingIndex = prevWords.findIndex(
          (selectedWord) => selectedWord.word === word
        );

        if (existingIndex !== -1) {
          // Remove the selected word
          const newWords = [...prevWords];
          newWords.splice(existingIndex, 1);
          
          // Reorder the remaining words
          return newWords.map((item, index) => ({
            word: item.word,
            order: index + 1,
          }));
        } else {
          // Add the new word
          const newWords = [...prevWords, { word, order: prevWords.length + 1 }];
          return newWords;
        }
      });
    },
    []
  );

  // Show preview screen before showing results
  const showPreview = useCallback(() => {
    console.log("Showing word preview screen");
    setShowingPreview(true);
  }, []);
  
  // Handle explicit submission - now returns a promise
  const handleSubmit = useCallback(async () => {
    console.log("Handle submit called with:", {
      selectedWordsLength: selectedWords.length,
      wordsLength: words.length
    });
    
    // Ensure we have enough words
    if (selectedWords.length < words.length) {
      console.log("Cannot submit - missing selections");
      return;
    }

    // Show preview first
    showPreview();
  }, [selectedWords, words.length, showPreview]);

  // Handle revealing results after preview - now also returns a promise
  const handleRevealResults = useCallback(async () => {
    console.log("Revealing results");
    setShowingPreview(false);
    
    // Extract just the words in order
    const orderedSelectedWords = [...selectedWords]
      .sort((a, b) => a.order - b.order)
      .map((item) => item.word);

    // Compare the selected words with the correct order
    const isOrderCorrect =
      JSON.stringify(orderedSelectedWords) === JSON.stringify(correctOrder);

    console.log("Submission analysis:", {
      isOrderCorrect,
      orderedSelectedWords,
      correctOrder
    });

    const result = isOrderCorrect ? "perfect" : "wrong";
    
    try {
      // Call onComplete and wait for it to finish
      await onComplete(result, elapsedTime, selectedWords, correctRule, uniqueLogicRules);
      console.log("Game data successfully saved");
    } catch (error) {
      console.error("Failed to save game data:", error);
      // Still in preview mode if save fails
      setShowingPreview(true);
      return;
    }
  }, [selectedWords, correctOrder, elapsedTime, onComplete, correctRule, uniqueLogicRules]);

  return {
    selectedWords,
    selectedRule,
    uniqueLogicRules,
    elapsedTime,
    handleWordClick,
    handleSubmit,
    setElapsedTime,
    showingPreview,
    handleRevealResults
  };
}
